import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Check, Loader2, AlertCircle, Home } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CheckoutSuccess = ({ onNavigateHome }) => {
    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [status, setStatus] = useState('checking'); // checking, success, error
    const [message, setMessage] = useState('Verifying your payment...');
    const [attempts, setAttempts] = useState(0);
    const maxAttempts = 10;
    const pollInterval = 2000;

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');

        if (!sessionId) {
            setStatus('error');
            setMessage('No checkout session found');
            return;
        }

        if (!isAuthenticated) {
            setStatus('error');
            setMessage('Please sign in to verify your payment');
            return;
        }

        const checkPaymentStatus = async () => {
            try {
                const headers = getAuthHeaders();
                const response = await axios.get(
                    `${BACKEND_URL}/api/checkout/status/${sessionId}`,
                    { headers }
                );

                if (response.data.payment_status === 'paid') {
                    setStatus('success');
                    setMessage(response.data.message || 'Welcome to Pro!');
                    // Refresh usage indicator
                    if (window.refreshUsage) {
                        window.refreshUsage();
                    }
                    return true;
                } else if (response.data.status === 'expired') {
                    setStatus('error');
                    setMessage('Payment session expired. Please try again.');
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Payment status check failed:', error);
                return false;
            }
        };

        const poll = async () => {
            const done = await checkPaymentStatus();
            if (!done && attempts < maxAttempts) {
                setAttempts(prev => prev + 1);
                setTimeout(poll, pollInterval);
            } else if (!done) {
                setStatus('error');
                setMessage('Unable to verify payment. Please check your email for confirmation or contact support.');
            }
        };

        poll();
    }, [isAuthenticated, getAuthHeaders, attempts]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md w-full"
            >
                <div className="glass-card p-8 text-center">
                    {status === 'checking' && (
                        <>
                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-violet-500/20 flex items-center justify-center">
                                <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
                            </div>
                            <h1 className="font-heading text-2xl font-bold text-white mb-2">
                                Processing Payment
                            </h1>
                            <p className="text-gray-400 mb-4">{message}</p>
                            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                                <motion.div
                                    className="h-full bg-violet-500"
                                    initial={{ width: '0%' }}
                                    animate={{ width: `${(attempts / maxAttempts) * 100}%` }}
                                    transition={{ duration: 0.3 }}
                                />
                            </div>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                                className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center"
                            >
                                <Crown className="w-10 h-10 text-white" />
                            </motion.div>
                            <h1 className="font-heading text-3xl font-bold text-white mb-2">
                                Welcome to Pro!
                            </h1>
                            <p className="text-gray-400 mb-6">{message}</p>
                            
                            <div className="space-y-3 mb-8 text-left">
                                <div className="flex items-center gap-3 text-sm">
                                    <Check className="w-5 h-5 text-green-400" />
                                    <span className="text-gray-300">30 blueprints per day unlocked</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <Check className="w-5 h-5 text-green-400" />
                                    <span className="text-gray-300">Unlimited saved blueprints</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                    <Check className="w-5 h-5 text-green-400" />
                                    <span className="text-gray-300">Continue & refine features enabled</span>
                                </div>
                            </div>

                            <Button
                                onClick={onNavigateHome}
                                className="w-full py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 rounded-xl font-semibold"
                                data-testid="go-home-btn"
                            >
                                <Home className="w-5 h-5 mr-2" />
                                Start Building
                            </Button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-destructive/20 flex items-center justify-center">
                                <AlertCircle className="w-8 h-8 text-destructive" />
                            </div>
                            <h1 className="font-heading text-2xl font-bold text-white mb-2">
                                Payment Issue
                            </h1>
                            <p className="text-gray-400 mb-6">{message}</p>
                            <Button
                                onClick={onNavigateHome}
                                variant="outline"
                                className="w-full"
                            >
                                Return Home
                            </Button>
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
};
